import { camelCase, isPlainObject, each, isInteger, forOwn, isDate } from 'lodash'
import { WrappedVariable, VariablePayload } from '@/store/workflow/types'
import mingo from 'mingo'
import Vue from 'vue'

export type Diff<T, W> = T extends W ? never : T
export type NotNull<T> = T extends null | undefined ? never : T

export const checkCondition = (condition: any, onObject: object | boolean): boolean => {
  if (typeof condition === 'boolean') {
    return condition
  } else if (condition === undefined) {
    return false
  } else if (condition.if) {
    const query = new mingo.Query(condition.if)
    return query.test(onObject)
  } else if (condition.unless) {
    const query = new mingo.Query(condition.unless)
    return !query.test(onObject)
  } else {
    throw new Error('condition object must have an if or an unless property')
  }
}

export const wrapVariables = (variables: VariablePayload): { [key: string]: WrappedVariable } => {
  const out: { [key: string]: WrappedVariable } = {}
  forOwn(variables, (value, key) => {
    switch (typeof value) {
      case 'boolean':
        out[key] = { value, type: 'Boolean' }
        break
      case 'string':
        out[key] = { value, type: 'String' }
        break
      case 'number':
        if (isInteger(value)) {
          out[key] = { value, type: 'Long' }
        } else {
          out[key] = { value, type: 'Double' }
        }
        break
      case 'bigint':
        out[key] = { value, type: 'Long' }
        break
      case 'object':
        if (value === null) {
          out[key] = { value, type: 'Null' }
        } else if (isDate(value)) {
          out[key] = { value: value.toISOString(), type: 'String' }
        } else {
          out[key] = value
        }
    }
  })
  return out
}

// https://www.typescriptlang.org/docs/handbook/release-notes/typescript-2-8.html
// tslint:disable: no-shadowed-variable
export type Unpacked<T> = T extends Array<infer U>
  ? U
  : T extends (...args: any[]) => infer U
  ? U
  : T extends Promise<infer U>
  ? U
  : T
// tslint:enable: no-shadowed-variable

function camelize(str: string, chars = '-') {
  return str.replace(new RegExp(`([${chars}]+)(.)`, 'g'), (match, p1, p2) => p2.toUpperCase())
}

// https://stackoverflow.com/questions/12931828/convert-returned-json-object-properties-to-lower-first-camelcase
export const camelizeKeys = (obj: any, chars = '-') => {
  if (Array.isArray(obj)) {
    return obj.map(v => camelizeKeys(v))
  } else if (isPlainObject(obj)) {
    return Object.keys(obj).reduce(
      (accumulator, key) => ({
        ...accumulator,
        [camelize(key, chars)]: camelizeKeys(obj[key], chars),
      }),
      {}
    )
  }
  return obj
}

export const readSettings = (fromFile: string) => {
  return camelizeKeys(require(fromFile))
}

export const hashToCamunda = (hash, prefix?: string): any => {
  const out = {}
  each(hash, (value, key) => {
    const newKey = prefix ? `${prefix}_${key}` : key
    switch (typeof value) {
      case 'bigint':
      case 'number':
        out[newKey] = { value, type: isInteger(value) ? 'Long' : 'Double' }
        break
      case 'boolean':
        out[newKey] = { value, type: 'Boolean' }
        break
      case 'object':
        if (value === null) {
          out[newKey] = { value, type: 'Null' }
          break
        }
      // eslint-disable-next-line no-fallthrough
      case 'string':
      default:
        out[newKey] = { value: value.toString(), type: 'String' }
    }
  })
  return camelizeKeys(out, '_')
}

interface CallbackObject {
  [x: string]: () => void
}

export interface EditIntent<I, O> {
  editObject: O
  withIntent: I
}

export interface IsErrorHandlingAction<E = any> {
  onError?: (error: E) => void
}

export interface IsErrorSink<E = any> {
  uncaughtErrors: E[]
}

export interface IsErrorHandlingMutation<E = any> {
  error: E
}

export function eventDelegatorFactory(toInstance: Vue, ...eventTypes: string[]): CallbackObject {
  const out: CallbackObject = {}
  eventTypes.forEach(eventType => {
    out[eventType] = () => {
      return toInstance.$emit(eventType, ...arguments)
    }
  })
  return out
}

export function kebabToCamelCase(input: any) {
  if (typeof input === 'object') {
    if (Array.isArray(input)) {
      return input.map(kebabToCamelCase)
    } else {
      const output = {}
      for (const [key, value] of Object.entries(input)) {
        const newKey = key.replace(/\-(.)/g, (match, g1) => g1.toUpperCase())
        output[newKey] = kebabToCamelCase(value)
      }
      return output
    }
  } else {
    return input
  }
}

export function hashToPostgrest(input: object) {
  const output = {}
  Object.entries(input).forEach(([k, v]) => (output[k] = 'eq.' + v.toString()))
  return output
}
